import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Table, multiSelect } from "../../../../../../components/Functional";
import { Button, Modal, getInput } from "../../../../../../components/ui";
import { REMOVE_SHIPMENT_WAREHOUSE_ITEMS_BY_IDS as removeShipmentWarehouseItemsByIdsMutation } from "../../../../../../graphql/mutation/Shipment";
import { UPDATE_WAREHOUSE_MOVE as updateWarehouseMoveMutation } from "../../../../../../graphql/mutation/WarehouseMove";
import { FETCH_ARTWORK_TARIFFS } from "../../../../../../graphql/query/ArtworkTariff";
import { FETCH_WAREHOUSE_CATEGORIES } from "../../../../../../graphql/query/WarehouseCategory";
import { FETCH_WAREHOUSE_LOCATIONS } from "../../../../../../graphql/query/WarehouseLocation";
import { FETCH_WAREHOUSE_MOVE_V2 as fetchWarehouseMoveV2 } from "../../../../../../graphql/query/WarehouseMove";
import SectionHeader from "../../../../../Warehouse/pages/Wizard/components/SectionHeader";
import Actions from "./components/Actions";
import AddItem from "./components/AddItem";
import AddWork from "./components/AddWork";

const buildWarehouseCategoryFilter = categoryId =>
  categoryId == null
    ? undefined
    : {
        categoryId: {
          operator: "eq",
          value: categoryId,
        },
      };

const buildWarehouseCategoryOptions = warehouseCategories => [
  { label: "All", value: null },
  ...(warehouseCategories || []).map(category => ({
    label: category.description,
    value: category.id,
  })),
];

const EditionNumber = ({ label }) => {
  const { editionNumberFormatted } = label || {};
  if (editionNumberFormatted) {
    return (
      <div className="flex flex-col">
        <span className="text-base font-medium leading-5 text-black">
          {editionNumberFormatted}
        </span>
      </div>
    );
  }
};

const TitleAndArtists = ({ values }) => {
  const title = values?.item?.instance?.edition?.artwork?.title;
  const artists =
    values?.item?.instance?.edition?.artwork?.artists
      ?.map(artist => artist?.name)
      .filter(Boolean)
      .join(", ") || "";
  const description = values?.item?.description;
  return (
    <div className="block min-h-full w-full break-words" title={description}>
      {title ? (
        <div className="break-words font-medium text-black">{title}</div>
      ) : null}
      {artists ? (
        <div className="mt-1 break-words text-xs text-black/60">{artists}</div>
      ) : null}
    </div>
  );
};

const Items = props => {
  const { shipment } = props || {};
  const { id: idParam } = useParams() || {};
  const shipmentId =
    shipment?.id ?? (idParam != null && idParam !== "" ? +idParam : undefined);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      limit: 50,
      offset: 0,
      _shipment: {
        id: { operator: "eq", value: shipmentId },
      },
      selected: { ids: {}, checked: false, select: null },
    },
  });

  const selectedIds = Object.values(formik?.values?.selected?.ids || {})
    .map(row => row?.id)
    .filter(id => id != null);
  const hasSelection = selectedIds.length > 0;
  const clearSelected = () =>
    formik.setFieldValue("selected", {
      ids: {},
      checked: false,
      select: null,
    });

  const [failedRemoveItems, setFailedRemoveItems] = useState([]);
  const [errorMessage, setMutationErrorMessage] = useState(null);

  const [
    removeShipmentWarehouseItemsByIds,
    {
      data: {
        removeShipmentWarehouseItemsByIds: {
          error: removeItemsError,
          success: removeItemsSuccess,
        } = {},
      } = {},
      loading: removeItemsLoading,
      reset: resetRemove,
    },
  ] = useMutation(removeShipmentWarehouseItemsByIdsMutation(), {
    refetchQueries: ["FetchWarehouseMoveV2"],
  });

  const removeSelectedModalProps = {
    title: removeItemsError ? "Error" : "Remove selected items",
    scale: "sm",
    description: removeItemsError
      ? removeItemsError
      : `Remove ${selectedIds.length} item(s) from this shipment?`,
    closeOnBackdrop: true,
    body: ({ closeModal }) => (
      <div className="mt-4 flex">
        <Button
          className="mr-3"
          label={removeItemsError ? "Close" : "Cancel"}
          action={removeItemsError ? "primary" : "default"}
          onClick={() => {
            resetRemove();
            closeModal?.();
          }}
        />
        {!removeItemsError && (
          <Button
            label={removeItemsLoading ? "Removing…" : "Remove"}
            disabled={removeItemsLoading}
            onClick={() => {
              removeShipmentWarehouseItemsByIds({
                variables: {
                  input: { shipmentId, warehouseMoveIds: selectedIds },
                },
                update: async (_, { data }) => {
                  const result = data?.removeShipmentWarehouseItemsByIds;
                  const failed = result?.failedRemoveItems ?? [];
                  const failedMoveIds = failed
                    .map(item => item?.moveId)
                    .filter(moveId => moveId != null);
                  const selectedEntries = Object.entries(
                    formik?.values?.selected?.ids || {},
                  );
                  const remainingSelectedEntries = selectedEntries.filter(
                    ([, row]) => failedMoveIds.includes(row?.id),
                  );
                  const remainingSelectedIds = remainingSelectedEntries.reduce(
                    (accumulator, [key, row]) => ({
                      ...accumulator,
                      [key]: row,
                    }),
                    {},
                  );

                  setFailedRemoveItems(failed);
                  if (failedMoveIds.length > 0) {
                    formik.setFieldValue("selected", {
                      ids: remainingSelectedIds,
                      checked: false,
                      select: null,
                    });
                  } else {
                    clearSelected();
                  }
                  resetRemove();
                  await closeModal?.();
                },
              });
            }}
          />
        )}
      </div>
    ),
  };

  const { data: { artworkTariffs = [] } = {} } = useQuery(
    FETCH_ARTWORK_TARIFFS,
  );
  const {
    data: { warehouseCategories = [] } = {},
    loading: warehouseCategoriesLoading,
  } = useQuery(FETCH_WAREHOUSE_CATEGORIES);
  const { data: { warehouseLocations = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_LOCATIONS,
  );
  const selectedWarehouseCategoryId =
    formik.values?._warehouseItem?.categoryId?.value ?? null;
  const warehouseCategoryInput = getInput({
    isLoading: warehouseCategoriesLoading,
    isNullable: false,
    label: "Item type",
    name: "warehouseCategoryId",
    onChange: categoryId => {
      formik.setValues({
        ...formik.values,
        _warehouseItem: buildWarehouseCategoryFilter(categoryId),
        offset: 0,
        selected: { ids: {}, checked: false, select: null },
      });
    },
    options: buildWarehouseCategoryOptions(warehouseCategories),
    type: "multi-select",
    value: selectedWarehouseCategoryId,
  });

  const multiSelectProps = {
    formik,
    label: "Items",
    name: "selected",
    keyName: "id",
  };

  const tableProps = {
    className: "sm:px-0 lg:px-0",
    dataKey: "warehouseMoveV2",
    formik,
    FETCH_QUERY: fetchWarehouseMoveV2({
      // WarehouseMove scalar fields
      confirmed: true,
      ti: true,
      value: true,
      bondref: true,
      taDays: true,
      siteId: true,
      goodsCode: true,
      user: true,
      shipmentId: true,
      // WarehouseMove associations
      site: true,
      locationId: true,
      item: {
        description: true,
        category: true,
        instance: {
          name: true,
          editionNumberFormatted: true,
          edition: {
            number: true,
            year: true,
            artwork: {
              title: true,
              artists: {
                name: true,
              },
            },
          },
        },
      },
    }),
    headers: [
      { ...multiSelect(multiSelectProps), width: "30px" },
      {
        label: "Item #",
        name: "item.id",
        sortKey: "id",
        type: "custom",
        width: "65px",
        component: ({ values }) => (
          <Link
            className={`underline ${!values?.confirmed ? "!text-[#FF7400]" : values?.ti === -1 ? "!text-[#8400FF]" : ""}`}
            to={`/warehouse/${values?.item?.id}/details`}
          >
            {values?.item?.id}
          </Link>
        ),
      },
      {
        label: "Category",
        name: "item.category.description",
        type: "label",
        width: "90px",
      },
      {
        label: "Title / Artist",
        name: "item",
        type: "custom",
        component: TitleAndArtists,
        width: "225px",
        rowClass: "w-[225px] max-w-[225px]",
        wrap: true,
      },
      {
        label: "Reference",
        name: "item.instance.name",
        type: "custom",
        width: "75px",
        component: ({ values }) => (
          <Link
            className="underline"
            to={`/instance/${values?.item?.instance?.id}/details`}
          >
            {values?.item?.instance?.name}
          </Link>
        ),
      },
      {
        label: "Edition",
        name: "item.instance",
        type: "custom",
        component: EditionNumber,
        width: "36px",
      },
      {
        label: "Year",
        name: "item.instance.edition.year",
        type: "label",
        width: "52px",
        wrap: true,
      },
      {
        inputType: "number-currency",
        label: "Value",
        name: "value",
        type: "input",
        allowEmpty: true,
        allowNegativeInput: false,
        numericCharactersOnly: true,
        width: "100px",
      },
      {
        designType: "threeStateCheckbox",
        inputType: "toggle",
        label: "CC",
        name: "ti",
        type: "input",
        width: "42px",
      },
      {
        inputType: "text",
        label: "Ref",
        name: "bondref",
        type: "input",
        width: "75px",
      },
      {
        inputType: "number-currency",
        label: "TA Days",
        name: "taDays",
        type: "input",
        inputClassName: "!w-[45px] min-w-0 max-w-[45px] px-1",
        allowDecimal: false,
        allowEmpty: true,
        allowNegativeInput: false,
        numericCharactersOnly: true,
        width: "48px",
        headerCellClassName: "!pr-0 whitespace-nowrap",
        rowClass: "w-[45px] max-w-[45px] !pr-0",
      },
      {
        label: "Location",
        name: "locationId",
        type: "custom",
        width: "160px",
        inputClassName: "w-full min-w-0",
        isNullable: true,
        component: props => {
          const { values } = props || {};

          const locations =
            warehouseLocations
              ?.filter(item => item?.id === 0 || item?.siteId == values?.siteId)
              ?.map(item => ({
                label: item?.description,
                value: item.id,
              })) || [];

          const input = getInput({
            ...props,
            label: "",
            type: "multi-select",
            options: locations,
          });

          return input;
        },
      },
      {
        type: "input",
        label: "Goods Code",
        name: "goodsCode",
        width: "120px",
        inputClassName: "min-w-[120px]",
        inputType: "multi-select",
        options: artworkTariffs?.map(tariff => ({
          label: [tariff?.code, tariff?.description]
            .filter(Boolean)
            .join(" — "),
          value: tariff?.code,
        })),
        formatOptionLabel: (option, { context }) =>
          context === "value" ? option.value : option.label,
      },
      {
        label: "User",
        name: "user",
        type: "label",
        width: "75px",
      },
      {
        label: "",
        name: "action",
        type: "custom",
        component: Actions,
        width: "60px",
      },
    ],
    UPDATE_MUTATION: updateWarehouseMoveMutation({
      // Return edited scalar fields so Apollo cache doesn't revert.
      bondref: true,
      goodsCode: true,
      locationId: true,
      siteId: true,
      taDays: true,
      ti: true,
      value: true,
      item: { category: {}, instance: { edition: { artwork: {} } } },
      site: true,
    }),
    variables: {
      _shipment: {
        id: {
          operator: "eq",
          value: shipmentId,
        },
      },
      // Whitelist the optional category filter stored in the external Formik
      // state. An undefined value represents "All".
      _warehouseItem: {},
    },
    skip: () => !shipmentId,
    variant: "dynamicHeight",
    allowColumnWrap: true,
    allowHorizontalScroll: true,
    onMutationError: error => {
      setMutationErrorMessage(
        error?.errors?.map?.(({ message }) => `${message} `) ||
          error?.graphQLErrors?.map?.(({ message }) => `${message} `) ||
          error?.message ||
          "An error occurred",
      );
    },
    sequentialRowUpdates: true,
  };

  const actions = [
    ...(hasSelection && shipmentId != null
      ? [
          {
            component: () => (
              <Modal {...removeSelectedModalProps}>
                <Button
                  label={`Remove selected (${selectedIds.length})`}
                  variant="default"
                />
              </Modal>
            ),
          },
        ]
      : []),
    {
      component: () => (
        <>
          <AddItem shipment={shipment ?? { id: shipmentId }} />
          <AddWork shipment={shipment ?? { id: shipmentId }} />
        </>
      ),
    },
  ];

  const failedRemoveModalProps = {
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: true,
    isOpen: failedRemoveItems.length > 0,
    onClose: () => setFailedRemoveItems([]),
    body: ({ closeModal }) => (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col">
          <div className="text-2xl font-bold">
            Some items could not be removed
          </div>
          <div className="mt-6">
            {failedRemoveItems.length} item(s) could not be removed from the
            shipment:
          </div>
          <div className="mt-4 max-h-64 overflow-y-auto rounded bg-[#2222220D] p-3 text-sm text-black">
            <ul className="flex list-disc flex-col gap-1 pl-5">
              {failedRemoveItems.map((item, index) => (
                <li key={item?.moveId ?? index}>
                  {item?.warehouseItemId != null ? (
                    <>
                      Item:{" "}
                      <Link
                        className="underline"
                        to={`/warehouse/${item.warehouseItemId}/movements`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        #{item.warehouseItemId}
                      </Link>
                    </>
                  ) : (
                    <span>Warehouse item</span>
                  )}
                  {item?.subsequentShipmentId != null ? (
                    <>
                      {" "}
                      (
                      <Link
                        className="underline"
                        to={`/shipments/${item.subsequentShipmentId}/items`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Shipment #{item.subsequentShipmentId}
                      </Link>
                      )
                    </>
                  ) : null}
                  {item?.message ? (
                    <span className="ml-1"> — {item.message}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-8 flex flex-row">
          <div className="flex flex-col">
            <div>
              <Button
                className="mr-4"
                label="Close"
                onClick={() => {
                  setFailedRemoveItems([]);
                  closeModal?.();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    ),
  };

  const mutationErrorModalProps = {
    scale: "sm",
    hideCloseButton: true,
    closeOnBackdrop: true,
    isOpen: errorMessage != null,
    onClose: () => {
      setMutationErrorMessage(null);
    },
    body: () => (
      <div className="mt-6 flex w-full flex-col">
        <div className="text-2xl font-bold">Error</div>
        <div className="mt-4 rounded bg-[#2222220D] p-3 text-sm text-red-500">
          {errorMessage}
        </div>
        <div className="mt-8 flex flex-row">
          <Button
            label="Close"
            onClick={() => {
              setMutationErrorMessage(null);
            }}
          />
        </div>
      </div>
    ),
  };

  return (
    <div className="w-full">
      <Modal {...failedRemoveModalProps} />
      <Modal {...mutationErrorModalProps} />
      <SectionHeader
        title={`Items - Shipment ${shipmentId ?? "…"}`}
        actions={actions}
      />
      <div className="flex flex-col gap-6 px-4 pb-2 sm:px-6 lg:flex-row lg:flex-wrap lg:items-end lg:gap-8 lg:px-8">
        <div className="w-full lg:w-96">
          <p className="mb-2">Unconfirmed Items</p>
          <p className="bg-[#2222220D] p-3 text-red-500">
            {shipment?.unconfirmedItemCount ?? "—"}
          </p>
        </div>
        <div className="w-full lg:w-72">{warehouseCategoryInput}</div>
      </div>
      <div className="mb-6 w-full">
        <Table {...tableProps} />
      </div>
    </div>
  );
};

export { buildWarehouseCategoryFilter, buildWarehouseCategoryOptions };
export default Items;
